import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Link,
  UnorderedList,
  ListItem,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  useColorModeValue,
  Button,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Code
} from '@chakra-ui/react';
import { ExternalLinkIcon, InfoIcon } from '@chakra-ui/icons';

const AboutPage: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBgColor = useColorModeValue('blue.50', 'blue.900');
  const addressColor = useColorModeValue('blue.600', 'blue.300');

  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" mb={2}>
            About SmartLaw Tokens
          </Heading>
          <Text fontSize="lg" color="gray.500">
            Analysis and information about the SmartLaw Tokens contract
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          <Stat
            p={5}
            shadow="md"
            border="1px"
            borderColor={borderColor}
            borderRadius="lg"
            bg={bgColor}
          >
            <StatLabel fontSize="md">Contract Address</StatLabel>
            <StatNumber fontSize="md" fontFamily="mono" color={addressColor}>
              0x3059B722579949459a7A778771Ec5e2e990392A1
            </StatNumber>
            <StatHelpText>
              <Link
                href="https://polygonscan.com/address/0x3059B722579949459a7A778771Ec5e2e990392A1"
                isExternal
                color="blue.500"
              >
                View on PolygonScan <ExternalLinkIcon mx="2px" />
              </Link>
            </StatHelpText>
          </Stat>

          <Stat
            p={5}
            shadow="md"
            border="1px"
            borderColor={borderColor}
            borderRadius="lg"
            bg={bgColor}
          >
            <StatLabel fontSize="md">Token Standard</StatLabel>
            <StatNumber fontSize="md">ERC-1155</StatNumber>
            <StatHelpText>Multi-Token Standard</StatHelpText>
          </Stat>

          <Stat
            p={5}
            shadow="md"
            border="1px"
            borderColor={borderColor}
            borderRadius="lg"
            bg={bgColor}
          >
            <StatLabel fontSize="md">Total Holders</StatLabel>
            <StatNumber fontSize="md">319</StatNumber>
            <StatHelpText>Token holders</StatHelpText>
          </Stat>
        </SimpleGrid>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Contract Overview
          </Heading>
          <Text mb={4}>
            The SmartLaw Tokens contract is an upgradeable ERC-1155 multi-token contract that implements
            a sophisticated role-based permission system. It's designed to manage tokenized permissions
            and digital assets with a focus on legal, governance, and administrative applications.
          </Text>
          <HStack wrap="wrap" spacing={3} mb={4}>
            <Badge colorScheme="blue">ERC1967 Proxy</Badge>
            <Badge colorScheme="green">Upgradeable</Badge>
            <Badge colorScheme="purple">ERC-1155</Badge>
            <Badge colorScheme="orange">Role-Based</Badge>
            <Badge colorScheme="teal">SmartLaw Tokens (WLX)</Badge>
          </HStack>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Primary Interacting Addresses
          </Heading>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Address</Th>
                <Th>Role/Activity</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td fontFamily="mono">0x00c69a89f6bdbb8af93745c80a99ed23344f5261</Td>
                <Td>Frequent interactions including minting and URI setting</Td>
              </Tr>
              <Tr>
                <Td fontFamily="mono">0x130093A5...45AfD8178</Td>
                <Td>Regular transaction sender</Td>
              </Tr>
              <Tr>
                <Td fontFamily="mono">0xA5F4fDE5...aE54ad5E0</Td>
                <Td>Active participant</Td>
              </Tr>
              <Tr>
                <Td fontFamily="mono">0xE303518fd573ea5ec2ca7cdafb928937a5831d4f</Td>
                <Td>Contract creator/deployer</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Common Usage Patterns
          </Heading>
          <UnorderedList spacing={3} pl={5}>
            <ListItem>
              <Text fontWeight="bold">Token Creation</Text>
              <Text>The <Code>create</Code> function is used to establish new token types with specified role permissions</Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="bold">Minting</Text>
              <Text>Frequent use of <Code>mint</Code> functions to issue tokens to addresses</Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="bold">Metadata Management</Text>
              <Text>Regular <Code>setURI</Code> calls to update token metadata</Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="bold">Permission Management</Text>
              <Text>Role-based access control via <Code>grantRole</Code> and other permission functions</Text>
            </ListItem>
          </UnorderedList>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Role Structure
          </Heading>
          <Text mb={4}>
            The contract implements a sophisticated role-based system with the following roles:
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
            {[
              'Superuser',
              'Minter',
              'Caster',
              'Transferer',
              'Configurer',
              'Maintainer',
              'Creator',
              'Limiter',
              'Burner',
              'Destroyer',
              'Oracle'
            ].map((role) => (
              <Box key={role} p={3} bg={sectionBgColor} borderRadius="md">
                <Text fontWeight="medium">{role}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Technical Implementation
          </Heading>
          <UnorderedList spacing={3} pl={5}>
            <ListItem>Uses an upgradeable proxy pattern (EIP-1967)</ListItem>
            <ListItem>Allows for contract evolution without address changes</ListItem>
            <ListItem>Supports both individual and bulk token operations</ListItem>
            <ListItem>Complies with ERC-1155 multi-token standard</ListItem>
          </UnorderedList>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Activity Timeline
          </Heading>
          <Text>
            The contract has seen significant activity clusters in September 2023 and January 2024,
            with recent transactions as of September 2024. Overall, it shows consistent but moderate
            transaction volume with approximately 83 transactions total.
          </Text>
        </Box>

        <Box p={6} bg={bgColor} shadow="md" borderRadius="lg">
          <Heading as="h2" size="md" mb={4}>
            Usage Implications
          </Heading>
          <Text>
            This contract is primarily used for creating and managing tokens with complex permission structures,
            suggesting its application in legal, governance, or administrative contexts. The role-based system
            allows for granular control over who can perform specific actions with tokens, making it suitable
            for situations where different users need different levels of access and permissions.
          </Text>
        </Box>

        <Flex justify="center" mt={4}>
          <HStack spacing={4}>
            <Button
              as={Link}
              href="https://polygonscan.com/address/0x3059B722579949459a7A778771Ec5e2e990392A1#readProxyContract"
              isExternal
              colorScheme="blue"
              rightIcon={<ExternalLinkIcon />}
            >
              Read Contract
            </Button>
            <Button
              as={Link}
              href="https://polygonscan.com/address/0x3059B722579949459a7A778771Ec5e2e990392A1#writeProxyContract"
              isExternal
              colorScheme="green"
              rightIcon={<ExternalLinkIcon />}
            >
              Write Contract
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </Container>
  );
};

export default AboutPage;